"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtSearchResult,
  ChainOfThoughtSearchResults,
  ChainOfThoughtStep,
} from "@/components/ai-elements/chain-of-thought";
import {
  Plan,
  PlanContent,
  PlanDescription,
  PlanHeader,
  PlanStep,
  PlanTitle,
  PlanTrigger,
} from "@/components/ai-elements/plan";
import {
  Task,
  TaskContent,
  TaskItem,
  TaskTrigger,
  type TaskStatus,
} from "@/components/ai-elements/task";
import { cn } from "@/lib/utils";
import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Check,
  Copy,
  Download,
  ExternalLink,
  File as FileIcon,
  FileCode2,
  Folder,
  FolderOpen,
  Monitor,
  Smartphone,
  Tablet,
  Terminal as TerminalIcon,
  X,
} from "lucide-react";

type StepStatus = "complete" | "active" | "pending";

export type MockWorkflowStage = "reasoning" | "tasks" | "plan" | "ide" | "preview" | "done";

export interface MockWorkflowReasoningStep {
  label: string;
  description?: string;
  status: StepStatus;
  searchResults?: string[];
}

export interface MockWorkflowTaskItem {
  title: string;
  status: TaskStatus;
}

export interface MockWorkflowPlanStep {
  label: string;
  description?: string;
  status: StepStatus;
}

export interface MockWorkflowFile {
  path: string;
  language: string;
  content: string;
}

export interface MockWorkflow {
  stage: MockWorkflowStage;
  reasoning: {
    title?: string;
    steps: MockWorkflowReasoningStep[];
  };
  tasks: {
    title?: string;
    items: MockWorkflowTaskItem[];
  };
  plan: {
    title: string;
    description?: string;
    steps: MockWorkflowPlanStep[];
  };
  planApproved: boolean;
  ide: {
    files: MockWorkflowFile[];
    activePath?: string | null;
    openPaths?: string[];
    terminalCollapsed?: boolean;
    terminal: string[];
  };
  preview: {
    html?: string | null;
  };
}

export default function MockWorkflowMessage({
  messageId: _messageId,
  workflow,
}: {
  messageId: string;
  workflow: MockWorkflow;
}) {
  const overallTaskStatus = useMemo<TaskStatus>(() => {
    const items = workflow.tasks.items || [];
    if (items.some((i) => i.status === "error")) return "error";
    if (items.some((i) => i.status === "in_progress")) return "in_progress";
    if (items.some((i) => i.status === "pending")) return "pending";
    return "completed";
  }, [workflow.tasks.items]);

  const completedTaskCount = useMemo(() => {
    return (workflow.tasks.items || []).filter((i) => i.status === "completed").length;
  }, [workflow.tasks.items]);

  const planSteps = workflow.plan.steps || [];
  const planStepCount = planSteps.length;

  return (
    <div className="flex flex-col gap-3">
      <ChainOfThought defaultOpen={workflow.stage === "reasoning"}>
        <ChainOfThoughtHeader>{workflow.reasoning.title || "Reasoning"}</ChainOfThoughtHeader>
        <ChainOfThoughtContent>
          {workflow.reasoning.steps.map((step, idx) => {
            const isLast = idx === workflow.reasoning.steps.length - 1;
            return (
              <ChainOfThoughtStep
                key={`${idx}-${step.label}`}
                label={step.label}
                description={step.description}
                status={step.status}
                isLast={isLast}
              >
                {step.searchResults && step.searchResults.length > 0 && (
                  <ChainOfThoughtSearchResults>
                    {step.searchResults.map((r) => (
                      <ChainOfThoughtSearchResult key={r}>{r}</ChainOfThoughtSearchResult>
                    ))}
                  </ChainOfThoughtSearchResults>
                )}
              </ChainOfThoughtStep>
            );
          })}
        </ChainOfThoughtContent>
      </ChainOfThought>

      {workflow.stage !== "reasoning" && (
        <Task defaultOpen={workflow.stage === "tasks"} status={overallTaskStatus}>
          <TaskTrigger
            title={workflow.tasks.title || "Tasks"}
            status={overallTaskStatus}
            completedCount={completedTaskCount}
            totalCount={workflow.tasks.items.length}
          />
          <TaskContent>
            {workflow.tasks.items.map((t, idx) => (
              <TaskItem key={`${idx}-${t.title}`} status={t.status}>
                {t.title}
              </TaskItem>
            ))}
          </TaskContent>
        </Task>
      )}

      {workflow.stage !== "reasoning" && workflow.stage !== "tasks" && (
        <Plan isStreaming={!workflow.planApproved && workflow.stage !== "plan"} defaultOpen={true}>
          <PlanHeader>
            <PlanTitle>{workflow.plan.title}</PlanTitle>
            {workflow.plan.description && <PlanDescription>{workflow.plan.description}</PlanDescription>}
          </PlanHeader>
          {planStepCount > 0 && (
            <>
              <PlanTrigger />
              <PlanContent>
                {workflow.plan.steps.map((s, idx) => (
                  <PlanStep
                    key={`${idx}-${s.label}`}
                    label={s.label}
                    description={s.description}
                    status={s.status}
                    isLast={idx === planStepCount - 1}
                  />
                ))}
              </PlanContent>
            </>
          )}
        </Plan>
      )}
    </div>
  );
}

export function MockWorkflowSidePanel({
  messageId: _messageId,
  workflow,
  tab,
}: {
  messageId: string;
  workflow: MockWorkflow;
  tab: "preview" | "ide";
}) {
  const [treeOpen, setTreeOpen] = useState<Record<string, boolean>>({});
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [activePath, setActivePath] = useState<string | null>(
    workflow.ide.activePath ?? (workflow.ide.files[0]?.path ?? null),
  );
  const [openPaths, setOpenPaths] = useState<string[]>(() => {
    const stored = workflow.ide.openPaths ?? [];
    if (stored.length > 0) return stored;
    const initial = workflow.ide.activePath ?? workflow.ide.files[0]?.path ?? null;
    return initial ? [initial] : [];
  });
  const [terminalCollapsed, setTerminalCollapsed] = useState<boolean>(
    workflow.ide.terminalCollapsed ?? false,
  );

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current != null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const tree = useMemo(() => buildFileTree(workflow.ide.files), [workflow.ide.files]);

  const activeFile = useMemo(() => {
    if (!activePath) return workflow.ide.files[0] || null;
    return workflow.ide.files.find((f) => f.path === activePath) || workflow.ide.files[0] || null;
  }, [activePath, workflow.ide.files]);

  const htmlFiles = useMemo(
    () => workflow.ide.files.filter((f) => f.path.toLowerCase().endsWith(".html")),
    [workflow.ide.files],
  );

  const previewFile = useMemo(() => {
    if (activeFile?.path?.toLowerCase().endsWith(".html")) return activeFile;
    if (htmlFiles.length === 1) return htmlFiles[0] || null;
    const byPreview =
      workflow.preview?.html && htmlFiles.length > 0 ? (htmlFiles[0] || null) : null;
    return byPreview;
  }, [activeFile, htmlFiles, workflow.preview?.html]);

  const previewHtml = useMemo(() => {
    if (previewFile?.content) return previewFile.content;
    return workflow.preview?.html ?? null;
  }, [previewFile, workflow.preview?.html]);

  const basename = (path: string) => {
    const parts = path.split("/").filter(Boolean);
    return parts[parts.length - 1] || path;
  };

  const openFile = (path: string) => {
    setActivePath(path);
    setOpenPaths((prev) => (prev.includes(path) ? prev : [...prev, path]));
  };

  const closeFileTab = (path: string) => {
    setOpenPaths((prev) => {
      const next = prev.filter((p) => p !== path);
      setActivePath((cur) => (cur === path ? (next[next.length - 1] ?? null) : cur));
      return next;
    });
  };

  const toggleTerminal = () => {
    setTerminalCollapsed((v) => !v);
  };

  useEffect(() => {
    const existingFiles = new Set(workflow.ide.files.map((f) => f.path));
    const first = workflow.ide.files[0]?.path ?? null;

    setOpenPaths((prev) => {
      const filtered = prev.filter((p) => existingFiles.has(p));
      if (filtered.length > 0) return filtered;
      return first ? [first] : [];
    });

    setActivePath((prev) => {
      if (prev && existingFiles.has(prev)) return prev;
      return first;
    });
  }, [workflow.ide.files]);

  const copyActiveFile = async () => {
    if (!activeFile) return;
    if (typeof window === "undefined" || !navigator?.clipboard?.writeText) return;
    if (isCopied) return;

    try {
      await navigator.clipboard.writeText(activeFile.content);
      setIsCopied(true);
      if (copyTimeoutRef.current != null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => setIsCopied(false), 2000);
    } catch {
      setIsCopied(false);
    }
  };

  const downloadActiveFile = () => {
    if (!activeFile) return;
    if (typeof window === "undefined") return;

    const blob = new Blob([activeFile.content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = basename(activeFile.path);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openPreviewInNewTab = () => {
    if (!previewHtml) return;
    if (typeof window === "undefined") return;
    const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(previewHtml)}`;
    window.open(dataUrl, "_blank", "noopener,noreferrer");
  };

  const downloadPreviewHtml = () => {
    if (!previewHtml) return;
    if (typeof window === "undefined") return;

    const blob = new Blob([previewHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = previewFile?.path ? basename(previewFile.path) : "preview.html";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const idePanel = (
    <div className={cn("h-full min-h-0 flex flex-col", !workflow.planApproved && "opacity-80")}>
      <div className="flex-1 min-h-0 grid grid-cols-[240px_1fr] overflow-hidden">
        <div className="border-r border-[color:var(--card-border)] overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 p-2 overflow-auto">
          {workflow.ide.files.length === 0 ? (
            <div className="h-full flex items-center justify-center px-3 py-6 text-xs theme-text-muted text-center">
              Nenhum arquivo gerado ainda.
            </div>
          ) : (
            <div className="space-y-1">
              {tree.children.map((node) => (
                <FileTreeNode
                  key={node.key}
                  node={node}
                  level={0}
                  open={treeOpen}
                  setOpen={setTreeOpen}
                  activePath={activeFile?.path || null}
                  onSelectFile={(path) => openFile(path)}
                />
              ))}
            </div>
          )}
          </div>
        </div>

        <div className="min-w-0 flex flex-col overflow-hidden">
          <div className="border-b border-[color:var(--card-border)] bg-[color:color-mix(in_oklab,var(--card-bg)_88%,transparent)] dark:border-zinc-800 dark:bg-[#0b0b10]">
            <div className="flex items-center gap-1 overflow-auto px-2 py-1.5">
              {openPaths.length > 0 ? (
                openPaths.map((path) => {
                  const isActive = (activePath ?? activeFile?.path ?? null) === path;
                  return (
                    <div
                      key={path}
                      className={cn(
                        "group flex items-center rounded-md border px-1.5 py-1",
                        "min-w-[120px] max-w-[240px]",
                        isActive
                          ? "border-[color:var(--card-border)] bg-[color:var(--card-bg)]"
                          : "border-transparent hover:border-[color:var(--card-border)] hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)]",
                        "dark:border-transparent dark:hover:border-zinc-800 dark:hover:bg-zinc-900/40",
                        isActive && "dark:border-zinc-800 dark:bg-zinc-950",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => openFile(path)}
                        className={cn(
                          "min-w-0 flex-1 truncate text-left font-mono text-[11px]",
                          isActive ? "text-[color:var(--foreground)]" : "text-[color:var(--foreground-muted)]",
                          isActive ? "dark:text-zinc-100" : "dark:text-zinc-400",
                        )}
                        title={path}
                      >
                        {basename(path)}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeFileTab(path);
                        }}
                        className={cn(
                          "ml-1 inline-flex size-6 items-center justify-center rounded-md text-[color:var(--foreground-muted)]",
                          "opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] hover:text-[color:var(--foreground)]",
                          "dark:text-zinc-500 dark:hover:bg-zinc-900/60 dark:hover:text-zinc-100",
                        )}
                        aria-label={`Fechar ${basename(path)}`}
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="px-2 py-1 text-[11px] text-[color:var(--foreground-muted)] dark:text-zinc-500">
                  Nenhum arquivo aberto.
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-[color:var(--card-border)] bg-[color:color-mix(in_oklab,var(--card-bg)_88%,transparent)] px-3 py-2 text-[11px] text-[color:var(--foreground-muted)] dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-400">
            <div className="min-w-0 flex items-center gap-2">
              <FileCode2 className="size-4 shrink-0 text-[color:var(--foreground-muted)] dark:text-zinc-500" />
              <span className="truncate font-mono text-[color:var(--foreground)] dark:text-zinc-100">
                {activeFile?.path || "Selecione um arquivo"}
              </span>
              {activeFile?.language ? (
                <span className="shrink-0 rounded border border-[color:var(--card-border)] px-1.5 py-0.5 uppercase dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300">
                  {activeFile.language}
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={downloadActiveFile}
                disabled={!activeFile}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border border-[color:var(--card-border)] px-2 py-1",
                  "text-[11px] text-[color:var(--foreground-muted)]",
                  "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] hover:text-[color:var(--foreground)] transition-colors",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                  "dark:border-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-900/60 dark:hover:text-zinc-100",
                )}
                title="Baixar"
              >
                <Download className="size-3.5" />
                Baixar
              </button>

              <button
                type="button"
                onClick={copyActiveFile}
                disabled={!activeFile}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border border-[color:var(--card-border)] px-2 py-1",
                  "text-[11px] text-[color:var(--foreground-muted)]",
                  "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] hover:text-[color:var(--foreground)] transition-colors",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                  "dark:border-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-900/60 dark:hover:text-zinc-100",
                )}
                title="Copiar"
              >
                {isCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                {isCopied ? "Copiado" : "Copiar"}
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto bg-[color:var(--chat-pre-bg)] dark:bg-zinc-950 dark:shadow-[inset_2px_0_0_var(--nt-primary)]">
            {activeFile ? (
              <pre className="m-0 p-4 font-mono text-[12px] leading-relaxed text-[color:var(--foreground)] dark:text-zinc-100">
                {activeFile.content.split("\n").map((line, idx) => (
                  <div key={idx} className="flex">
                    <span className="w-10 shrink-0 pr-4 text-right text-[color:var(--foreground-muted)] select-none dark:text-zinc-600">
                      {idx + 1}
                    </span>
                    <span className="min-w-0 flex-1 whitespace-pre break-words">
                      {line === "" ? "\u200b" : line}
                    </span>
                  </div>
                ))}
              </pre>
            ) : (
              <div className="h-full flex items-center justify-center p-4 text-xs theme-text-muted text-center">
                Nenhum arquivo selecionado.
              </div>
            )}
          </div>

          <div className="border-t border-zinc-800 bg-zinc-950 text-zinc-100 dark:border-zinc-800 dark:bg-[#0b0b10]">
            <div className="px-4 py-2 flex items-center justify-between gap-2 border-b border-zinc-800 dark:border-zinc-800">
              <div className="flex items-center gap-2 text-sm text-zinc-400 dark:text-zinc-300">
                <TerminalIcon className="h-4 w-4" />
                Terminal
              </div>
              <div className="flex items-center gap-2">
                <div className="text-xs text-zinc-400 dark:text-zinc-400">
                  {workflow.planApproved ? "Executado" : "Aguardando"}
                </div>
                <button
                  type="button"
                  onClick={toggleTerminal}
                  className={cn(
                    "inline-flex items-center justify-center size-7 rounded-md border border-zinc-800",
                    "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60 transition-colors",
                  )}
                  aria-label={terminalCollapsed ? "Expandir terminal" : "Minimizar terminal"}
                  title={terminalCollapsed ? "Expandir" : "Minimizar"}
                >
                  {terminalCollapsed ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                </button>
              </div>
            </div>
            {!terminalCollapsed ? (
              <div className="max-h-[240px] overflow-auto p-4 font-mono text-[12px] leading-relaxed">
                {(workflow.ide.terminal || []).length > 0 ? (
                  <pre className="whitespace-pre-wrap break-words text-zinc-100 dark:text-zinc-100">
                    {(workflow.ide.terminal || []).map((line, idx) => (
                      <span key={idx} className="block">
                        <span className="text-zinc-500 select-none dark:text-zinc-600">
                          {String(idx + 1).padStart(2, "0")}{" "}
                        </span>
                        {line}
                      </span>
                    ))}
                  </pre>
                ) : (
                  <div className="text-zinc-400 dark:text-zinc-400">Sem saída ainda.</div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  const previewPanel = (
    <div className="h-full min-h-0 flex flex-col">
      <div className="px-3 py-2 border-b theme-border flex items-center gap-2 bg-[color:var(--card-bg)]">
        <div className="min-w-0 flex items-center gap-2 text-xs theme-text">
          <Monitor className="w-4 h-4 shrink-0" />
          <span className="truncate">
            {previewFile?.path ? basename(previewFile.path) : "Preview"}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPreviewDevice("mobile")}
            className={cn(
              "inline-flex items-center justify-center w-9 h-9 rounded-lg border theme-border theme-card",
              "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] transition-colors",
              previewDevice === "mobile" &&
                "bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)]",
            )}
            aria-label="Mobile"
            title="Mobile"
          >
            <Smartphone className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setPreviewDevice("tablet")}
            className={cn(
              "inline-flex items-center justify-center w-9 h-9 rounded-lg border theme-border theme-card",
              "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] transition-colors",
              previewDevice === "tablet" &&
                "bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)]",
            )}
            aria-label="Tablet"
            title="Tablet"
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setPreviewDevice("desktop")}
            className={cn(
              "inline-flex items-center justify-center w-9 h-9 rounded-lg border theme-border theme-card",
              "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] transition-colors",
              previewDevice === "desktop" &&
                "bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)]",
            )}
            aria-label="Desktop"
            title="Desktop"
          >
            <Monitor className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={downloadPreviewHtml}
            disabled={!previewHtml}
            className={cn(
              "inline-flex items-center justify-center w-9 h-9 rounded-lg border theme-border theme-card",
              "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] transition-colors",
              "disabled:opacity-60 disabled:cursor-not-allowed",
            )}
            aria-label="Baixar HTML"
            title="Baixar HTML"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={openPreviewInNewTab}
            disabled={!previewHtml}
            className={cn(
              "inline-flex items-center justify-center w-9 h-9 rounded-lg border theme-border theme-card",
              "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] transition-colors",
              "disabled:opacity-60 disabled:cursor-not-allowed",
            )}
            aria-label="Abrir em nova aba"
            title="Abrir em nova aba"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-zinc-950">
        {previewHtml ? (
          <div
            className="h-full w-full overflow-auto p-4"
            style={{
              background: "color-mix(in oklab, var(--chat-pre-bg) 55%, transparent)",
            }}
          >
            <div
              className={cn(
                "mx-auto h-full min-h-[360px] w-full bg-white rounded-xl overflow-hidden",
                "border border-[color:var(--card-border)] shadow-[0_18px_50px_rgba(0,0,0,.22)]",
                "dark:bg-white",
              )}
              style={{
                maxWidth:
                  previewDevice === "mobile"
                    ? 390
                    : previewDevice === "tablet"
                      ? 820
                      : 1200,
              }}
            >
              <iframe
                title="Preview"
                className="w-full h-full"
                srcDoc={previewHtml}
                sandbox="allow-scripts allow-forms allow-modals"
              />
            </div>
          </div>
        ) : (
          <div className="h-full p-4 text-sm theme-text-muted">
            {htmlFiles.length > 0
              ? "Selecione um arquivo HTML no painel IDE para visualizar."
              : "Aguardando preview..."}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full min-h-0">
      {tab === "ide" ? idePanel : previewPanel}
    </div>
  );
}

type FileTreeNodeType =
  | { kind: "folder"; key: string; name: string; children: FileTreeNodeType[] }
  | { kind: "file"; key: string; name: string; path: string };

function buildFileTree(files: MockWorkflowFile[]) {
  const root: { kind: "folder"; key: string; name: string; children: FileTreeNodeType[] } = {
    kind: "folder",
    key: "root",
    name: "root",
    children: [],
  };

  const getOrCreateFolder = (children: FileTreeNodeType[], key: string, name: string) => {
    const existing = children.find((c) => c.kind === "folder" && c.key === key);
    if (existing && existing.kind === "folder") return existing;
    const folder: FileTreeNodeType = { kind: "folder", key, name, children: [] };
    children.push(folder);
    return folder as Extract<FileTreeNodeType, { kind: "folder" }>;
  };

  for (const file of files) {
    const parts = file.path.split("/").filter(Boolean);
    let cur = root;
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i]!;
      const isLast = i === parts.length - 1;
      if (isLast) {
        cur.children.push({ kind: "file", key: file.path, name: part, path: file.path });
      } else {
        const folderKey = parts.slice(0, i + 1).join("/");
        cur = getOrCreateFolder(cur.children, folderKey, part);
      }
    }
  }

  const sortNode = (n: FileTreeNodeType): FileTreeNodeType => {
    if (n.kind === "file") return n;
    const folders = n.children.filter((c) => c.kind === "folder").map(sortNode);
    const filesOnly = n.children.filter((c) => c.kind === "file");
    folders.sort((a, b) => a.name.localeCompare(b.name));
    filesOnly.sort((a, b) => a.name.localeCompare(b.name));
    return { ...n, children: [...folders, ...filesOnly] };
  };

  return sortNode(root) as Extract<FileTreeNodeType, { kind: "folder" }>;
}

function FileTreeNode({
  node,
  level,
  open,
  setOpen,
  activePath,
  onSelectFile,
}: {
  node: FileTreeNodeType;
  level: number;
  open: Record<string, boolean>;
  setOpen: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  activePath: string | null;
  onSelectFile: (path: string) => void;
}) {
  if (node.kind === "file") {
    const isActive = node.path === activePath;
    return (
      <div
        className={cn(
          "flex cursor-pointer items-center gap-1 rounded px-2 py-1 transition-colors",
          "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)]",
          isActive && "bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)]",
          "text-[color:var(--foreground)]",
        )}
        onClick={() => onSelectFile(node.path)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onSelectFile(node.path);
          }
        }}
        role="treeitem"
        tabIndex={0}
        title={node.path}
      >
        <span className="size-4 shrink-0" style={{ marginLeft: level * 14 }} />
        <span className="shrink-0">
          <FileIcon className="size-4 text-[color:var(--foreground-muted)]" />
        </span>
        <span className="truncate text-[12px]">{node.name}</span>
      </div>
    );
  }

  const isOpen = open[node.key] ?? true;
  return (
    <div>
      <div
        className={cn(
          "flex w-full items-center gap-1 rounded px-2 py-1 text-left transition-colors",
          "hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)]",
          "text-[color:var(--foreground)]",
        )}
        role="treeitem"
        tabIndex={0}
        title={node.name}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setOpen((prev) => ({ ...prev, [node.key]: !isOpen }));
          }
        }}
      >
        <button
          className="flex shrink-0 cursor-pointer items-center border-none bg-transparent p-0"
          type="button"
          onClick={() => setOpen((prev) => ({ ...prev, [node.key]: !isOpen }))}
          aria-label={isOpen ? "Fechar pasta" : "Abrir pasta"}
        >
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-[color:var(--foreground-muted)] transition-transform",
              isOpen && "rotate-90",
            )}
            style={{ marginLeft: level * 14 }}
          />
        </button>
        <button
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-left"
          onClick={() => setOpen((prev) => ({ ...prev, [node.key]: !isOpen }))}
          type="button"
        >
          <span className="shrink-0">
            {isOpen ? (
              <FolderOpen className="size-4 text-[color:var(--nt-primary)]" />
            ) : (
              <Folder className="size-4 text-[color:var(--nt-primary)]" />
            )}
          </span>
          <span className="truncate text-[12px]">{node.name}</span>
        </button>
      </div>
      {isOpen && (
        <div className="ml-4 border-l border-[color:var(--card-border)] pl-2 mt-1 space-y-1">
          {node.children.map((c) => (
            <FileTreeNode
              key={c.key}
              node={c}
              level={level + 1}
              open={open}
              setOpen={setOpen}
              activePath={activePath}
              onSelectFile={onSelectFile}
            />
          ))}
        </div>
      )}
    </div>
  );
}
